import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import type { AppliedPromo } from '../../../../../../types/common';
import { ACTIVATING_DISCOUNT_MANAGEMENT } from '../../../../../../lib/cartPage/config';
import { toMoney } from '../../utils/money';
import './stickySummary.scss';

type Props = {
    isAuthenticated: boolean;
    payableTotal: number;
    goodsPayable: number;
    baseGoodsTotal: number;
    originalTotal: number;
    effectiveDeliveryMethod: 'pickup' | 'dpd' | 'dtl';
    effectiveShippingCost: number | null;
    shippingStatusText: string;
    discountNotice: React.ReactNode | null;
    birthdayEligible: boolean;
    bonusEligible: number | null;
    showDiscountsUnavailableNotice: boolean;
    selectedDiscount: 'birthday' | 'bonus' | null;
    hasSecondPillowPromo: boolean;
    secondPillowSaved: number;
    onSelectDiscount: (v: 'birthday' | 'bonus' | null) => void;
    showPromoInput: boolean;
    promoCode: string;
    promoError: string;
    appliedPromo: AppliedPromo | null;
    onPromoChange: (v: string) => void;
    onPromoApply: () => void;
    onPromoCancel: () => void;
    isLoading: boolean;
    onPlaceOrder: () => void;
    placeOrderDisabled: boolean;
};

export const StickySummary: React.FC<Props> = ({
    isAuthenticated,
    payableTotal,
    goodsPayable,
    baseGoodsTotal,
    originalTotal,
    effectiveDeliveryMethod,
    effectiveShippingCost,
    shippingStatusText,
    discountNotice,
    birthdayEligible,
    bonusEligible,
    showDiscountsUnavailableNotice,
    selectedDiscount,
    hasSecondPillowPromo,
    secondPillowSaved,
    onSelectDiscount,
    showPromoInput,
    promoCode,
    promoError,
    appliedPromo,
    onPromoChange,
    onPromoApply,
    onPromoCancel,
    isLoading,
    onPlaceOrder,
    placeOrderDisabled,
}) => {
    const [agree, setAgree] = useState<boolean>(false);

    useEffect(() => {
        setAgree(isAuthenticated ? true : false);
    }, [isAuthenticated]);

    const saved = useMemo(() => {
        const base = +(Number(baseGoodsTotal) - Number(goodsPayable)).toFixed(2);
        const safeBase = base > 0 ? base : 0;
        return hasSecondPillowPromo ? Math.max(safeBase, Number(secondPillowSaved) || 0) : safeBase;
    }, [goodsPayable, baseGoodsTotal, hasSecondPillowPromo, secondPillowSaved]);

    const deliveryLabel = useMemo(() => (effectiveDeliveryMethod === 'pickup' ? 'Самовывоз' : 'Доставка'), [effectiveDeliveryMethod]);

    const deliveryValue = useMemo(() => {
        if (effectiveDeliveryMethod === 'pickup') return 'Бесплатно';
        if (shippingStatusText) return shippingStatusText;
        if (effectiveShippingCost == null) return 'рассчитывается...';
        if (effectiveShippingCost === 0) return 'Бесплатно';
        return `${toMoney(effectiveShippingCost)} ₽`;
    }, [effectiveDeliveryMethod, effectiveShippingCost, shippingStatusText]);

    // const discountsDisabled = hasSecondPillowPromo;
    const hasDiscountOptions = birthdayEligible || (bonusEligible != null && bonusEligible > 0);

    return (
        <div className="stickySummary">
            <div className="stickySummary__box">
                <div className="stickySummary__title">К оплате</div>
                <div className="stickySummary__total" aria-label="Сумма к оплате">
                    <span className="stickySummary__totalNum">{toMoney(payableTotal)}</span>
                    <span className="stickySummary__totalCur">₽</span>
                </div>
            </div>

            <div className="stickySummary__rows">
                <div className="stickySummary__row">
                    <span className="stickySummary__rowLabel">Стоимость товаров</span>
                    <span className="stickySummary__rowValue">{toMoney(goodsPayable)} ₽</span>
                </div>
                <div className="stickySummary__row">
                    <span className="stickySummary__rowLabel">{deliveryLabel}</span>
                    <span className="stickySummary__rowValue">{deliveryValue}</span>
                </div>
            </div>

            <div className="stickySummary__promo" aria-label="Промокод">
                {/* {appliedPromo?.code ? (
                    <>
                        <div className="stickySummary__promoApplied">
                            <span className="stickySummary__promoCode">{appliedPromo.code}</span>
                            <button type="button" className="stickySummary__promoCancel" onClick={onPromoCancel}>
                                <img className="stickySummary__promoCancel_btn" src="/icons/new/promo_cancel.svg" alt="Отменить" />
                            </button>
                        </div>
                        {discountNotice ? <div className="stickySummary__notice">{discountNotice}</div> : null}
                    </>
                ) : showPromoInput && !discountsDisabled ? (
                    <>
                        <div className="stickySummary__promoInputRow">
                            <input
                                className={`stickySummary__promoInput ${promoError ? 'stickySummary__promoInput--error' : ''}`}
                                value={promoCode}
                                onChange={(e) => onPromoChange(e.target.value)}
                                placeholder={showPromoInput && !discountsDisabled ? 'Промокод' : 'Промокод недоступен'}
                            />
                            {promoCode && (
                                <button type="button" className="stickySummary__promoApply" onClick={onPromoApply} disabled={!promoCode || isLoading}>
                                    <img className="stickySummary__promoApply_btn" src="/icons/new/promo_enter.svg" alt="Применить" />
                                </button>
                            )}
                        </div>
                        {promoError ? <div className="stickySummary__promoError">{promoError}</div> : null}
                    </>
                ) : null} */}
                {appliedPromo?.code ? (
                    <>
                        <div className="stickySummary__promoApplied">
                            <span className="stickySummary__promoCode">{appliedPromo.code}</span>
                            <button type="button" className="stickySummary__promoCancel" onClick={onPromoCancel}>
                                <img className="stickySummary__promoCancel_btn" src="/icons/new/promo_cancel.svg" alt="Отменить" />
                            </button>
                        </div>
                        {discountNotice ? <div className="stickySummary__notice">{discountNotice}</div> : null}
                    </>
                ) : showPromoInput ? (
                    <>
                        <div className="stickySummary__promoInputRow">
                            <input
                                className={`stickySummary__promoInput ${promoError ? 'stickySummary__promoInput--error' : ''}`}
                                value={promoCode}
                                onChange={(e) => onPromoChange(e.target.value)}
                                placeholder="Промокод"
                            />
                            {promoCode && (
                                <button type="button" className="stickySummary__promoApply" onClick={onPromoApply} disabled={!promoCode || isLoading}>
                                    <img className="stickySummary__promoApply_btn" src="/icons/new/promo_enter.svg" alt="Применить" />
                                </button>
                            )}
                        </div>
                        {promoError ? <div className="stickySummary__promoError">{promoError}</div> : null}
                    </>
                ) : null}
            </div>

            {ACTIVATING_DISCOUNT_MANAGEMENT ? (
                <div className="stickySummary__discounts" aria-label="Скидки">
                    {/* {showDiscountsUnavailableNotice && <div className="stickySummary__discountsTitle">Скидки</div>} */}
                    {hasDiscountOptions ? (
                        // (
                        //     // <div className="stickySummary__mutedBlock">Скидки и промо-код недоступны вместе с акцией на вторую подушку.</div>
                        //     <div className="stickySummary__mutedBlock">Скидка ко дню рождения и списание бонусов недоступны вместе с акцией на вторую подушку.</div>
                        // ) :
                        <div className="stickySummary__discountOptions" role="radiogroup" aria-label="Тип скидки">
                            {birthdayEligible && (
                                <label className={`stickySummary__opt ${selectedDiscount === 'birthday' ? 'stickySummary__opt--active' : ''}`}>
                                    <input
                                        type="radio"
                                        name="discountType"
                                        checked={selectedDiscount === 'birthday'}
                                        onChange={() => onSelectDiscount('birthday')}
                                    />
                                    <span className="stickySummary__optMark" aria-hidden="true" />
                                    <span className="stickySummary__optText">Скидка ко дню рождения -25%</span>
                                </label>
                            )}
                            {bonusEligible != null && bonusEligible > 0 && (
                                <label className={`stickySummary__opt ${selectedDiscount === 'bonus' ? 'stickySummary__opt--active' : ''}`}>
                                    <input type="radio" name="discountType" checked={selectedDiscount === 'bonus'} onChange={() => onSelectDiscount('bonus')} />
                                    <span className="stickySummary__optMark" aria-hidden="true" />
                                    <span className="stickySummary__optText">Списать бонусы: {toMoney(bonusEligible)} ₽</span>
                                </label>
                            )}
                            {(birthdayEligible || (bonusEligible != null && bonusEligible > 0)) && selectedDiscount !== null && (
                                <button type="button" className="stickySummary__clearDiscount" onClick={() => onSelectDiscount(null)}>
                                    Снять скидку
                                </button>
                            )}
                        </div>
                    ) : showDiscountsUnavailableNotice ? (
                        <div className="stickySummary__mutedBlock">Для выбранных товаров скидка ко дню рождения и списание бонусов недоступны.</div>
                    ) : null}
                </div>
            ) : null}

            <div className="stickySummary__row">
                <span className="stickySummary__rowLabel">Ваша скидка</span>
                <span className="stickySummary__rowValue">{saved > 0 ? `${toMoney(saved)} ₽` : '0 ₽'}</span>
            </div>

            <button
                type="button"
                className="stickySummary__cta"
                onClick={onPlaceOrder}
                disabled={
                    placeOrderDisabled || isLoading
                    // || !agree
                }
            >
                {isLoading ? 'Оформляем…' : 'Оформить заказ'}
            </button>

            {!isAuthenticated ? (
                <label className="stickySummary__agree">
                    <input className="stickySummary__checkbox" type="checkbox" checked={agree} onChange={(e) => setAgree(e.target.checked)} />
                    <span className="stickySummary__checkboxBox" aria-hidden="true" />
                    <span className="stickySummary__agreeText">
                        Согласен с условиями{' '}
                        <Link className="stickySummary__agreeLink" to="/customers?section=policy" target="_blank" rel="noopener noreferrer">
                            обработки данных
                        </Link>
                        ,{' '}
                        <Link className="stickySummary__agreeLink" to="/customers?section=return" target="_blank" rel="noopener noreferrer">
                            пользовательского соглашения
                        </Link>
                        , и{' '}
                        <Link className="stickySummary__agreeLink" to="/customers?section=offer" target="_blank" rel="noopener noreferrer">
                            оферты
                        </Link>
                    </span>
                </label>
            ) : null}
        </div>
    );
};
